import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import apiClient from '../api/client';

const AsyncTableSearch = ({
  value,
  onChange,
  onResults,
  onActiveChange,
  onLoadingChange,
  onPaginationChange, // Новый callback для пагинации: ({ page, pages, total, perPage }) => void
  placeholder = 'Поиск...',
  searchDoc,
  searchFields = ['name'],
  staticFilters = {},
  minLength = 2,
  debounceMs = 300,
  page = 1, // Текущая страница для поиска
  perPage = 20, // Количество результатов на страницу
  sx = {},
}) => {
  const [query, setQuery] = useState(value || '');
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef(null);

  // --- NEW: предотвращает цикл "value → query → value"
  const internalUpdateRef = useRef(false);
  
  // Ref для отслеживания предыдущего active состояния (предотвращает бесконечный цикл)
  const prevActiveRef = useRef(null);

  // Refs для callback-функций — чтобы useEffect не перезапускался
  const onResultsRef = useRef(onResults);
  const onLoadingChangeRef = useRef(onLoadingChange);
  const onChangeRef = useRef(onChange);
  const onActiveChangeRef = useRef(onActiveChange);
  const onPaginationChangeRef = useRef(onPaginationChange);

  useEffect(() => {
    onResultsRef.current = onResults;
    onLoadingChangeRef.current = onLoadingChange;
    onChangeRef.current = onChange;
    onActiveChangeRef.current = onActiveChange;
    onPaginationChangeRef.current = onPaginationChange;
  });

  // Memo keys
  const searchFieldsKey = useMemo(() => JSON.stringify(searchFields), [searchFields]);
  const staticFiltersKey = useMemo(() => JSON.stringify(staticFilters), [staticFilters]);

  // --- FIX: синхронизация внешнего value → query без циклов
  useEffect(() => {
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return; // пропускаем внутренние изменения
    }
    if (value !== query) {
      setQuery(value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Active state - вызываем onActiveChange ТОЛЬКО если значение изменилось
  useEffect(() => {
    const active = !!(query && query.trim().length >= minLength);
    if (prevActiveRef.current !== active) {
      prevActiveRef.current = active;
      onActiveChangeRef.current?.(active);
    }
  }, [query, minLength]);

  // =============== SEARCH EFFECT WITH DEBOUNCE ===============
  useEffect(() => {
    let ignore = false;
    let timeoutId;

    const performSearch = async () => {
      if (!searchDoc || !searchFields.length) return;

      if (controllerRef.current) {
        try {
          controllerRef.current.abort();
        } catch {}
      }

      const abortCtrl = new AbortController();
      controllerRef.current = abortCtrl;

      try {
        setLoading(true);
        if (onLoadingChangeRef.current) onLoadingChangeRef.current(true);

        const params = {
          page: page,
          per_page: perPage,
          q: query,
          fields: searchFields.join(','),
          with_pagination: '1', // Запрашиваем данные с пагинацией
          ...staticFilters,
        };

        const response = await apiClient.get(
          `/documents/${encodeURIComponent(searchDoc)}/search`,
          { params, signal: abortCtrl.signal }
        );

        if (!ignore) {
          // Ответ может быть в формате { data: [], page, pages, total, per_page }
          const responseData = response.data;
          let arr;
          let paginationInfo = null;

          if (responseData && typeof responseData === 'object' && Array.isArray(responseData.data)) {
            // Новый формат с пагинацией
            arr = responseData.data;
            paginationInfo = {
              page: responseData.page || 1,
              pages: responseData.pages || 1,
              total: responseData.total || arr.length,
              perPage: responseData.per_page || perPage,
            };
          } else {
            // Старый формат - просто массив
            arr = Array.isArray(responseData) ? responseData : [];
            paginationInfo = {
              page: 1,
              pages: 1,
              total: arr.length,
              perPage: perPage,
            };
          }

          const normalized = arr.map((item) =>
            item && (item.id != null || item._id != null)
              ? { ...item, id: item.id ?? item._id }
              : item
          );

          if (onResultsRef.current) onResultsRef.current(normalized);
          if (onPaginationChangeRef.current) onPaginationChangeRef.current(paginationInfo);
        }
      } catch (error) {
        if (!ignore) {
          if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
            console.error('Search error:', error);
            if (onResultsRef.current) onResultsRef.current([]);
            if (onPaginationChangeRef.current) {
              onPaginationChangeRef.current({ page: 1, pages: 1, total: 0, perPage });
            }
          }
        }
      } finally {
        if (!ignore) {
          setLoading(false);
          if (onLoadingChangeRef.current) onLoadingChangeRef.current(false);
        }
      }
    };

    const active = !!(query && query.trim().length >= minLength);

    if (!active) {
      // НЕ вызываем onResults([]) здесь - пусть onActiveChange в родителе решает что показать
      // Это предотвращает конфликт между onResults([]) и onActiveChange(false) -> setFilteredDeals(deals)
      setLoading(false);
      onLoadingChangeRef.current?.(false);
      return;
    }

    timeoutId = setTimeout(performSearch, debounceMs);

    return () => {
      ignore = true;
      clearTimeout(timeoutId);
      if (controllerRef.current) {
        try {
          controllerRef.current.abort();
        } catch {}
      }
    };
  }, [query, searchDoc, searchFieldsKey, staticFiltersKey, minLength, debounceMs, page, perPage]);

  // ------------------ INPUT HANDLER ------------------
  const handleInput = (e) => {
    const newValue = e.target.value;
    internalUpdateRef.current = true; // помечаем как локальное обновление
    setQuery(newValue);

    if (onChangeRef.current) {
      onChangeRef.current(newValue); // вызывает setSearchQuery в DealsPage
    }
  };

  return (
    <TextField
      fullWidth
      placeholder={placeholder}
      value={query}
      onChange={handleInput}
      sx={{
        mb: 0,
        ...sx,
        '& .MuiOutlinedInput-root': {
          color: '#FFFFFF',
          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
          '&.Mui-focused fieldset': { borderColor: '#FFFFFF' },
        },
        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
        '& .MuiInputBase-input::placeholder': {
          color: 'rgba(255, 255, 255, 0.5)',
          opacity: 1,
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        ),
        endAdornment: loading ? (
          <InputAdornment position="end">
            <span style={{ fontSize: 12, opacity: 0.7, color: '#FFFFFF' }}>
              Поиск…
            </span>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
};

export default AsyncTableSearch;
